'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '@/stores/app-store';
import { isTempDevice, kindOf, resolveBand, shortName } from '@/lib/coldchain';
import { simMonthlyTemp, type SimSpec } from '@/lib/facility-sim';

/*
 * แบบบันทึกอุณหภูมิตู้เย็นเก็บวัคซีน — the Thai EPI monthly record, one month
 * per logger, rendered as the two sheets the paper form has:
 *
 *   แผ่นที่ 1 — กราฟบันทึก : a 09.00 chart and a 15.00 chart per metric, one
 *                            column per day, the in-range band left white
 *                            between a red zone above and a blue zone below.
 *   แผ่นที่ 2 — ตารางบันทึก : the same readings as numbers, with สถานะ and an
 *                            empty หมายเหตุ column to write the correction in.
 *
 * A logger that reports humidity as well (the ห้องยา units do, on field_no 2)
 * gets its own pair of charts and its own table columns. Each metric is judged
 * against ITS OWN configured limits: the pharmacy room's 60 %RH cap has nothing
 * to do with its 20–35 °C window, and humidity often has only an upper limit —
 * hence a band here is a pair of independently-nullable edges, not a range.
 *
 * Readings come from /api/devices/[mac]/monthly-temp, which picks the reading
 * nearest each target time inside the tolerance. A slot with nothing in that
 * window is deliberately left EMPTY rather than filled from a neighbouring
 * hour: the form is a compliance record, and a blank box someone completes by
 * hand is honest where an invented number is not.
 *
 * Both sheets are styled with literal colours, not theme tokens — they are
 * paper, white in either theme, and printing copies their markup into a
 * standalone iframe where CSS variables of the app would not resolve.
 */

// ── sheet palette (fixed: this is paper, not app chrome) ────────────────────
const INK = '#111';
const MUTED = '#555';
const RULE = '#333';
const GRID = '#d9d9d9';
const OVER_FILL = '#fbe4e4'; // above the upper limit
const UNDER_FILL = '#e4ecfa'; // below the lower limit
const OK = '#1a9d5a';
const BAD = '#d23b3b';

const TH_MONTHS = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

type MetricKey = 'temp' | 'humid';

interface SlotValue { value: number; time: string }
interface DayRow { day: number; am: SlotValue | null; pm: SlotValue | null }
interface ApiMetric {
  key: MetricKey;
  no: number; name: string; unit: string;
  upper: number | null; lower: number | null;
  rows: DayRow[];
}
interface ReportData {
  macId: string;
  year: number; month: number; days: number;
  am: string; pm: string; tol: number;
  metrics: ApiMetric[];
}

/** Everything a metric needs to draw and to be judged, resolved once. */
interface MetricView {
  key: MetricKey;
  labelTh: string; labelEn: string;
  unit: string;
  lo: number | null; hi: number | null;
  /** false when the kind fallback supplied the limits (temperature only). */
  fromDevice: boolean;
  yMin: number; yMax: number; step: number;
  /** temperature prints +8 / -20; humidity prints 60. */
  signed: boolean;
  digits: number;
  rows: DayRow[];
}

/** "09:00" as shown on the form: Thai writes the hour with a dot. */
const dotTime = (hhmm: string) => hhmm.replace(':', '.');

/** Axis and limit labels: +8 / -20 for temperature, 60 for humidity. */
const sign = (v: number, signed: boolean) => (signed && v > 0 ? `+${v}` : String(v));

/** In range = inside every edge the device actually set. */
const inRange = (v: number, m: { lo: number | null; hi: number | null }) =>
  (m.lo == null || v >= m.lo) && (m.hi == null || v <= m.hi);

/** "20 ถึง 35 °C" · "ไม่เกิน 60 %RH" · "" when nothing is configured. */
function limitLabel(m: MetricView, th: boolean): string {
  const u = m.unit;
  if (m.lo != null && m.hi != null) return `${sign(m.lo, m.signed)} ${th ? 'ถึง' : 'to'} ${sign(m.hi, m.signed)} ${u}`;
  if (m.hi != null) return `${th ? 'ไม่เกิน' : 'max'} ${sign(m.hi, m.signed)} ${u}`;
  if (m.lo != null) return `${th ? 'ไม่ต่ำกว่า' : 'min'} ${sign(m.lo, m.signed)} ${u}`;
  return th ? 'ยังไม่ได้ตั้งเกณฑ์' : 'no range set';
}

/**
 * อำเภอ / จังหวัด out of the plant's free-text address, so the header can fill
 * them in where the address carries them. Anything not found stays a blank rule
 * for someone to write on — better than printing a wrong district.
 */
function parseAddr(addr: string | undefined, kind: 'amphoe' | 'province'): string {
  if (!addr) return '';
  const re = kind === 'amphoe'
    ? /(?:อ\.|อำเภอ|เขต)\s*([^\s,]+)/
    : /(?:จ\.|จังหวัด)\s*([^\s,]+)/;
  return re.exec(addr)?.[1] ?? '';
}

/** A dotted rule for a field the system cannot fill — written in by hand. */
function BlankRule({ width = 90 }: { width?: number }) {
  return <span style={{ display: 'inline-block', width, borderBottom: `1px dotted ${MUTED}` }} />;
}

// ── the daily grid chart ────────────────────────────────────────────────────

const LABEL_W = 40;
const COL_W = 42;
const HEAD_H = 22;
/**
 * Height per GRID LINE, not per unit — humidity rules every 5 %RH. Scaled so a
 * chart lands around 360–380px whatever its axis spans: a fridge's 2…8 band
 * draws 18 rules and a pharmacy room's 20…35 draws 27, and at a fixed 20px the
 * latter would run a chart off the printed page on its own.
 */
const rowHeight = (lines: number) => Math.max(11, Math.min(20, Math.round(380 / lines)));

function GridChart({ metric, slot, days, compact = false }: { metric: MetricView; slot: 'am' | 'pm'; days: number; compact?: boolean }) {
  const { yMin, yMax, step, lo, hi, rows } = metric;
  const lines = Math.max(1, Math.round((yMax - yMin) / step));
  // printing squeezes the rows so both charts of sheet 1 fit one landscape A4 (the text keeps its size)
  const ROW_H = rowHeight(lines) * (compact ? 0.6 : 1);
  const w = LABEL_W + days * COL_W;
  const h = HEAD_H + lines * ROW_H;
  const y = (v: number) => HEAD_H + ((yMax - v) / step) * ROW_H;
  const cx = (day: number) => LABEL_W + (day - 0.5) * COL_W;

  const ticks: number[] = [];
  for (let i = 0; i <= lines; i++) ticks.push(yMax - i * step);

  // Points, and the segments joining them. Days with no reading break the line
  // rather than being bridged — a gap on this form means "not recorded", and a
  // line drawn across it would read as a measurement that never happened.
  const pts = rows
    .map((r) => ({ day: r.day, v: r[slot]?.value }))
    .filter((p): p is { day: number; v: number } => typeof p.v === 'number');
  const segments: Array<Array<{ day: number; v: number }>> = [];
  let run: Array<{ day: number; v: number }> = [];
  for (const p of pts) {
    if (run.length > 0 && p.day !== run[run.length - 1].day + 1) {
      segments.push(run);
      run = [];
    }
    run.push(p);
  }
  if (run.length > 0) segments.push(run);

  // A value outside the drawn axis still has to appear — clamp it to the edge
  // so an excursion shows as a dot on the boundary instead of vanishing.
  const clamp = (v: number) => Math.min(yMax, Math.max(yMin, v));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ display: 'block', height: 'auto', overflow: 'visible' }} role="img">
      {/* out-of-range zones behind everything; an unset edge shades nothing */}
      {hi != null && hi < yMax && (
        <rect x={LABEL_W} y={y(yMax)} width={days * COL_W} height={y(hi) - y(yMax)} fill={OVER_FILL} />
      )}
      {lo != null && lo > yMin && (
        <rect x={LABEL_W} y={y(lo)} width={days * COL_W} height={y(yMin) - y(lo)} fill={UNDER_FILL} />
      )}

      {/* day numbers along the top */}
      {Array.from({ length: days }, (_, i) => (
        <text key={`d${i}`} x={cx(i + 1)} y={HEAD_H - 7} textAnchor="middle" fontSize="10" fill={MUTED}>{i + 1}</text>
      ))}

      {/* one horizontal rule per step; the limit lines are drawn heavier */}
      {ticks.map((v) => {
        const edge = v === lo || v === hi;
        return (
          <g key={`y${v}`}>
            <line x1={LABEL_W} x2={w} y1={y(v)} y2={y(v)} stroke={edge ? RULE : GRID} strokeWidth={edge ? 1.2 : 0.6} />
            {/* compact (print): label only every 5th rule + the limits, so the squeezed axis stays legible */}
            {(!compact || edge || lines <= 16 || (Math.round(v / step) % 5 === 0 && [lo, hi].every((e) => e == null || Math.abs(v - e) > step * 2))) && (
              <text x={LABEL_W - 6} y={y(v) + 3.5} textAnchor="end" fontSize="9.5" fontWeight={edge ? 700 : 400} fill={edge ? INK : MUTED}>
                {sign(v, metric.signed)}
              </text>
            )}
          </g>
        );
      })}

      {/* a limit that falls between two rules still needs its line */}
      {[lo, hi].map((v) =>
        v == null || ticks.includes(v) || v < yMin || v > yMax ? null : (
          <line key={`lim${v}`} x1={LABEL_W} x2={w} y1={y(v)} y2={y(v)} stroke={RULE} strokeWidth={1.2} />
        )
      )}

      {/* day column separators */}
      {Array.from({ length: days + 1 }, (_, i) => (
        <line key={`x${i}`} x1={LABEL_W + i * COL_W} x2={LABEL_W + i * COL_W} y1={HEAD_H} y2={h} stroke={GRID} strokeWidth={0.6} />
      ))}
      <rect x={LABEL_W} y={HEAD_H} width={days * COL_W} height={h - HEAD_H} fill="none" stroke={RULE} strokeWidth={1} />

      {segments.map((seg, i) => (
        <polyline
          key={`s${i}`}
          points={seg.map((p) => `${cx(p.day)},${y(clamp(p.v))}`).join(' ')}
          fill="none" stroke={OK} strokeWidth={1.2}
        />
      ))}
      {pts.map((p) => (
        <circle key={`p${p.day}`} cx={cx(p.day)} cy={y(clamp(p.v))} r={3} fill={inRange(p.v, metric) ? OK : BAD} />
      ))}
    </svg>
  );
}

// ── sheet furniture shared by both pages ────────────────────────────────────

function SheetHead({
  plantName, addr, devLabel, macId, monthLabel, beYear, metrics, sheet, subtitle, th,
}: {
  plantName: string; addr?: string; devLabel: string; macId: string;
  monthLabel: string; beYear: number; metrics: MetricView[];
  sheet: string; subtitle: string; th: boolean;
}) {
  const amphoe = parseAddr(addr, 'amphoe');
  const province = parseAddr(addr, 'province');
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: INK }}>
            {th ? 'แบบบันทึกอุณหภูมิตู้เย็นเก็บวัคซีน' : 'Vaccine Refrigerator Temperature Log'}
          </div>
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>{subtitle} — {sheet}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>
            {th ? 'ประจำเดือน' : 'Month of'} {monthLabel} {beYear}
          </div>
          {metrics.map((m) => (
            <div key={m.key} style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>
              {th ? 'เกณฑ์' : 'Range'}{metrics.length > 1 ? ` ${th ? m.labelTh : m.labelEn}` : ''} {limitLabel(m, th)}
            </div>
          ))}
        </div>
      </div>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '4px 22px', fontSize: 12, color: INK,
        borderBottom: `1.5px solid ${INK}`, paddingBottom: 8, marginTop: 10,
      }}>
        <div><b>{th ? 'หน่วยบริการ:' : 'Facility:'}</b> {plantName}</div>
        <div><b>{th ? 'อำเภอ:' : 'District:'}</b> {amphoe || <BlankRule />}</div>
        <div><b>{th ? 'จังหวัด:' : 'Province:'}</b> {province || <BlankRule />}</div>
        <div><b>{th ? 'ตู้เย็น/เครื่องวัด:' : 'Unit / logger:'}</b> {devLabel} ({macId})</div>
      </div>
    </>
  );
}

function Notes({ metrics, amTime, pmTime, tol, th }: { metrics: MetricView[]; amTime: string; pmTime: string; tol: number; th: boolean }) {
  const temp = metrics.find((m) => m.key === 'temp');
  const humid = metrics.find((m) => m.key === 'humid');
  const items: string[] = th ? [
    `บันทึกวันละ 2 ครั้ง เวลา ${dotTime(amTime)} น. และ ${dotTime(pmTime)} น. ทุกวันไม่เว้นวันหยุด`,
    `อุณหภูมิที่เหมาะสมสำหรับเก็บวัคซีนคือ ${temp ? limitLabel(temp, th) : '—'} หากอยู่นอกช่วงนี้ให้รีบแก้ไขและบันทึกสาเหตุ/การแก้ไขในช่องหมายเหตุ`,
    ...(humid ? [`ความชื้นสัมพัทธ์ในห้องเก็บยาต้อง ${limitLabel(humid, th)} หากเกินให้ระบายอากาศ/เดินเครื่องลดความชื้น และบันทึกไว้ในช่องหมายเหตุ`] : []),
    `ค่าในแบบฟอร์มนี้ดึงอัตโนมัติจากเครื่องวัด (IoT) โดยเลือกค่าที่ใกล้เวลา ${dotTime(amTime)} น. (±${tol} นาที) และ ${dotTime(pmTime)} น. (±${tol} นาที) มากที่สุด หากไม่มีค่าในช่วงเวลาดังกล่าว ช่องนั้นจะเว้นว่างไว้ให้บันทึกด้วยมือ`,
  ] : [
    `Record twice a day at ${amTime} and ${pmTime}, every day including holidays.`,
    `The safe storage range is ${temp ? limitLabel(temp, th) : '—'}. Outside it, correct immediately and write the cause and action in the notes column.`,
    ...(humid ? [`Relative humidity in the pharmacy room must stay ${limitLabel(humid, th)}. Above it, ventilate or dehumidify and note it.`] : []),
    `Values are pulled automatically from the IoT logger, taking the reading closest to ${amTime} (±${tol} min) and ${pmTime} (±${tol} min). A slot with no reading in that window is left blank to be filled in by hand.`,
  ];
  return (
    <ol style={{ margin: '10px 0 0', paddingLeft: 20, fontSize: 10.5, color: MUTED, lineHeight: 1.75 }}>
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ol>
  );
}

function SignRow({ th }: { th: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 24,
      marginTop: 20, paddingTop: 14, borderTop: `1px solid ${GRID}`, fontSize: 12, color: INK,
    }}>
      <div>{th ? 'ผู้บันทึก' : 'Recorded by'} ...........................................</div>
      <div>{th ? 'ผู้ตรวจสอบ' : 'Verified by'} ...........................................</div>
      <div>{th ? 'วันที่' : 'Date'} ......../......../........</div>
    </div>
  );
}

function Sheet({ children }: { children: React.ReactNode }) {
  return (
    <section className="tr-sheet" style={{
      background: '#fff', color: INK, border: `1px solid ${GRID}`, borderRadius: 10,
      padding: '22px 26px 26px', marginBottom: 18,
      fontFamily: "'Noto Sans Thai','Plus Jakarta Sans',sans-serif",
    }}>
      {children}
    </section>
  );
}

// ── the page ────────────────────────────────────────────────────────────────

export default function MonthlyTempReportPage() {
  const currentPlant = useAppStore((s) => s.currentPlant);
  const plants = useAppStore((s) => s.plants);
  const th = useAppStore((s) => s.lang) === 'th';
  const plant = currentPlant ? plants[currentPlant] : null;

  // Only temperature loggers can produce this form; a plant's power meters are
  // not offered in the picker at all.
  const devices = useMemo(() => (plant?.devices ?? []).filter(isTempDevice), [plant]);

  const [macId, setMacId] = useState<string>('');
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [amTime, setAmTime] = useState('09:00');
  const [pmTime, setPmTime] = useState('15:00');
  const [tol, setTol] = useState(30);
  const [showSettings, setShowSettings] = useState(false);
  // on screen: the modern summary by default; the paper form on demand (it is what prints either way)
  const [view, setViewRaw] = useState<'summary' | 'form'>(() => {
    try { return localStorage.getItem('mtr.view') === 'form' ? 'form' : 'summary'; } catch { return 'summary'; }
  });
  const setView = (v: 'summary' | 'form') => { setViewRaw(v); try { localStorage.setItem('mtr.view', v); } catch { /* private mode */ } };

  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefer a fridge — that is what the form is for — but fall back to whatever
  // temperature logger the plant does have.
  useEffect(() => {
    if (devices.length === 0) { setMacId(''); return; }
    if (devices.some((d) => d.sn === macId)) return;
    const preferred = devices.find((d) => d.st === 'online' && kindOf(d.name || '') === 'fridge') ?? devices.find((d) => d.st === 'online') ?? devices[0];
    setMacId(preferred.sn);
  }, [devices, macId]);

  const load = useCallback(async () => {
    if (!macId) { setData(null); return; }
    // a sales-demo logger (staticDevice with simItem, e.g. the globen hospital demo): readings from lib/facility-sim
    const simItem = (devices.find((d) => d.sn === macId) as { simItem?: { min?: number; max?: number; sim?: SimSpec } } | undefined)?.simItem;
    if (simItem) { setError(null); setData(simMonthlyTemp(macId, simItem, year, month, amTime, pmTime, tol) as ReportData); return; }
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        year: String(year), month: String(month), am: amTime, pm: pmTime, tol: String(tol),
      });
      const res = await fetch(`/api/devices/${encodeURIComponent(macId)}/monthly-temp?${qs}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setData(json as ReportData);
    } catch (e: unknown) {
      setData(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [macId, year, month, amTime, pmTime, tol, devices]);

  useEffect(() => { void load(); }, [load]);

  const device = devices.find((d) => d.sn === macId);
  const devLabel = device ? shortName(device.name || device.sn) : macId;

  /**
   * Per-metric drawing + judging config.
   *
   * Temperature falls back to the WHO band for its sensor kind when the device
   * has none configured. Humidity does NOT: there is no kind-implied humidity
   * window, and inventing one would fail readings against a number nobody set.
   * Its axis is the full 0–100 %RH ruled every 5, rather than temperature's
   * six degrees of headroom ruled every 1.
   */
  const metricViews = useMemo<MetricView[]>(() => {
    if (!data) return [];
    return data.metrics.map((m): MetricView => {
      if (m.key === 'temp') {
        const { band, fromDevice } = resolveBand(device?.name || '', m.lower, m.upper);
        const lo = band ? band[0] : null;
        const hi = band ? band[1] : null;
        return {
          key: 'temp', labelTh: 'อุณหภูมิ', labelEn: 'Temperature',
          unit: /c/i.test(m.unit || '') ? '°C' : (m.unit || '°C'),
          lo, hi, fromDevice,
          yMin: lo != null ? lo - 6 : 0,
          yMax: hi != null ? hi + 6 : 40,
          step: 1, signed: true, digits: 1, rows: m.rows,
        };
      }
      return {
        key: 'humid', labelTh: 'ความชื้นสัมพัทธ์', labelEn: 'Humidity',
        unit: '%RH',
        lo: m.lower, hi: m.upper, fromDevice: m.lower != null || m.upper != null,
        yMin: 0, yMax: 100, step: 5, signed: false, digits: 0, rows: m.rows,
      };
    });
  }, [data, device]);

  const tempView = metricViews.find((m) => m.key === 'temp');
  const humidView = metricViews.find((m) => m.key === 'humid');
  const days = data?.days ?? new Date(year, month, 0).getDate();

  /**
   * Slots that should already have been recorded. For a month in progress only
   * the target times that have passed count, so "4/4" on the 2nd means both
   * readings on both days — not 4 out of a whole month that has not happened.
   */
  const dueSlots = useMemo(() => {
    const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;
    if (!isCurrent) return year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1) ? 0 : days * 2;
    const today = now.getDate();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
    let n = (today - 1) * 2;
    if (nowMin >= mins(amTime)) n++;
    if (nowMin >= mins(pmTime)) n++;
    return n;
  }, [year, month, days, now, amTime, pmTime]);

  /** Recorded / out-of-range / min / max for one metric. */
  const statsFor = useCallback((m: MetricView | undefined) => {
    let recorded = 0, out = 0;
    let min: number | undefined, max: number | undefined;
    for (const r of m?.rows ?? []) {
      for (const s of [r.am, r.pm]) {
        if (!s) continue;
        recorded++;
        if (!inRange(s.value, m!)) out++;
        if (min === undefined || s.value < min) min = s.value;
        if (max === undefined || s.value > max) max = s.value;
      }
    }
    return { recorded, out, min, max, missing: Math.max(0, dueSlots - recorded) };
  }, [dueSlots]);

  // The headline counters stay on temperature: that is the vaccine-compliance
  // number the form exists for. Humidity gets its own line on sheet 2.
  const stats = useMemo(() => statsFor(tempView), [statsFor, tempView]);
  const humidStats = useMemo(() => (humidView ? statsFor(humidView) : null), [statsFor, humidView]);

  const monthLabel = (th ? TH_MONTHS : EN_MONTHS)[month - 1];
  const beYear = year + 543;
  const subtitle = th
    ? `บันทึกวันละ 2 ครั้ง เวลา ${dotTime(amTime)} น. และ ${dotTime(pmTime)} น.`
    : `Twice daily at ${amTime} and ${pmTime}`;

  // ── print / save as PDF ──
  // The sheets already look like the paper, so printing copies their markup
  // into a standalone iframe rather than re-templating the whole form. That
  // works only because every sheet colour is a literal: a CSS variable would
  // have nothing to resolve against in a fresh document.
  const sheetsRef = useRef<HTMLDivElement>(null);
  // Print renders the sheets once in their compact form, then copies that markup (see the effect below).
  const [printing, setPrinting] = useState(false);
  const doPrint = () => setPrinting(true);
  useEffect(() => {
    if (!printing) return;
    printSheets();
    setPrinting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printing]);
  const printSheets = () => {
    const node = sheetsRef.current;
    if (!node) return;
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    if (!doc) { frame.remove(); return; }
    const title = `${th ? 'บันทึกอุณหภูมิ' : 'Temperature log'} ${devLabel} ${monthLabel} ${beYear}`;
    doc.open();
    doc.write(`<!doctype html><html lang="${th ? 'th' : 'en'}"><head><meta charset="utf-8"><title>${title}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@400;600;700&display=swap');
  /* margin 0 makes the browser drop its own date / title / URL header and footer; the page margin is the body padding */
  @page { size: A4 landscape; margin: 0; }
  html, body { margin: 0; background: #fff; }
  body { padding: 8mm 10mm; }
  .tr-sheet ol { font-size: 9.5px !important; line-height: 1.5 !important; margin-top: 6px !important; }
  .tr-sheet > div[style*="justify-content: space-between"] { margin-top: 10px !important; padding-top: 8px !important; }
  .tr-chart { margin-top: 8px !important; }
  body { font-family: 'Noto Sans Thai','Sarabun','Tahoma',sans-serif; color: ${INK}; }
  .tr-sheet { border: none !important; border-radius: 0 !important; padding: 0 0 6mm !important; margin: 0 !important; }
  /* Each sheet starts a page, but a sheet carrying four charts (a logger that
     reports humidity as well) is taller than one landscape A4 — let it flow and
     keep only the individual charts and tables from being split down the middle. */
  .tr-sheet + .tr-sheet { break-before: page; }
  .tr-chart, table { break-inside: avoid; }
</style></head><body>${node.innerHTML}</body></html>`);
    doc.close();
    const go = () => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      setTimeout(() => frame.remove(), 1000);
    };
    // Give the webfont and the inlined SVG a moment before the print dialog
    // freezes the frame; onload alone fires before @import resolves.
    if (doc.readyState === 'complete') setTimeout(go, 400);
    else frame.onload = () => setTimeout(go, 400);
  };

  if (!plant) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
        {th ? 'เลือกสถานที่ก่อน' : 'Select a plant first'}
      </div>
    );
  }

  const selectStyle: React.CSSProperties = {
    padding: '8px 12px', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
    borderRadius: 9, border: '1px solid var(--glass-border)',
    background: 'var(--bg-elevated)', color: 'var(--text-primary)', cursor: 'pointer',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── toolbar (screen only — not part of the printed sheets) ── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={macId} onChange={(e) => setMacId(e.target.value)} style={{ ...selectStyle, minWidth: 220 }}>
          {devices.length === 0 && <option value="">{th ? 'ไม่มีเครื่องวัดอุณหภูมิ' : 'No temperature logger'}</option>}
          {/* online loggers first, then offline — the dot shows the state (a native <option> can't be coloured) */}
          {(['online', 'offline'] as const).map((st) => {
            const list = devices.filter((d) => (d.st === 'online') === (st === 'online'));
            if (!list.length) return null;
            return (
              <optgroup key={st} label={st === 'online' ? (th ? `ออนไลน์ (${list.length})` : `Online (${list.length})`) : (th ? `ออฟไลน์ (${list.length})` : `Offline (${list.length})`)}>
                {list.map((d) => (
                  <option key={d.sn} value={d.sn}>{st === 'online' ? '🟢' : '⚪'} {shortName(d.name || d.sn)}{st === 'offline' ? (th ? ' · ออฟไลน์' : ' · offline') : ''}</option>
                ))}
              </optgroup>
            );
          })}
        </select>
        {device && (
          <span title={device.st === 'online' ? undefined : (th ? 'เครื่องวัดนี้ไม่ได้ส่งข้อมูล' : 'This logger is not reporting')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700,
              color: device.st === 'online' ? '#0f9f6e' : '#b45309', background: device.st === 'online' ? 'rgba(16,183,127,.12)' : 'rgba(245,158,11,.14)' }}>
            <span style={{ width: 8, height: 8, borderRadius: 8, background: device.st === 'online' ? '#10b77f' : '#f59e0b' }} />
            {device.st === 'online' ? (th ? 'ออนไลน์' : 'Online') : (th ? 'ออฟไลน์' : 'Offline')}
          </span>
        )}

        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={selectStyle}>
          {(th ? TH_MONTHS : EN_MONTHS).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>

        <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={selectStyle}>
          {Array.from({ length: 6 }, (_, i) => now.getFullYear() - 4 + i).map((y) => (
            <option key={y} value={y}>{th ? y + 543 : y}</option>
          ))}
        </select>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
          fontSize: 12, borderRadius: 9, border: '1px solid var(--glass-border)', color: 'var(--text-secondary)',
        }}>
          <span style={{ color: 'var(--text-muted)' }}>{th ? 'เวลาที่ตั้งค่า' : 'Target times'}</span>
          <b style={{ color: 'var(--text-primary)' }}>{th ? 'เช้า' : 'AM'} {amTime} (±{tol})</b>
          <span style={{ color: 'var(--text-muted)' }}>·</span>
          <b style={{ color: 'var(--text-primary)' }}>{th ? 'บ่าย' : 'PM'} {pmTime} (±{tol})</b>
          <button
            onClick={() => setShowSettings((v) => !v)}
            style={{
              marginLeft: 4, padding: '2px 8px', fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit',
              borderRadius: 7, border: '1px solid var(--glass-border)', background: 'transparent',
              color: 'var(--text-secondary)', cursor: 'pointer',
            }}
          >
            {th ? 'ตั้งค่า' : 'Settings'}
          </button>
        </div>

        <div style={{ display: 'inline-flex', padding: 3, borderRadius: 10, border: '1px solid var(--glass-border)', background: 'var(--bg-surface)' }}>
          {(['summary', 'form'] as const).map((v) => (
            <button key={v} onClick={() => setView(v)}
              style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'inherit', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: view === v ? 'var(--accent)' : 'transparent', color: view === v ? '#fff' : 'var(--text-secondary)' }}>
              {v === 'summary' ? (th ? 'ภาพรวม' : 'Overview') : (th ? 'แบบฟอร์ม' : 'Paper form')}
            </button>
          ))}
        </div>

        <button
          onClick={doPrint}
          disabled={!data}
          style={{
            padding: '9px 18px', fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit',
            borderRadius: 9, border: 'none', background: 'var(--amber)', color: '#1a1200',
            cursor: data ? 'pointer' : 'not-allowed', opacity: data ? 1 : 0.5,
          }}
        >
          {th ? 'พิมพ์ / บันทึกเป็น PDF' : 'Print / Save as PDF'}
        </button>

        <button
          onClick={() => void load()}
          title={th ? 'โหลดใหม่' : 'Reload'}
          style={{
            padding: '8px 11px', borderRadius: 9, border: '1px solid var(--glass-border)',
            background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', lineHeight: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
          </svg>
        </button>

        <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <span>{th ? 'บันทึกได้' : 'Recorded'} <b style={{ color: 'var(--text-primary)' }}>{stats.recorded}/{dueSlots}</b> {th ? 'ครั้ง' : ''}</span>
          <span>{th ? 'นอกเกณฑ์' : 'Out of range'} <b style={{ color: stats.out > 0 ? 'var(--red)' : 'var(--text-primary)' }}>{stats.out}</b></span>
          <span>{th ? 'ไม่มีข้อมูล' : 'Missing'} <b style={{ color: stats.missing > 0 ? 'var(--amber)' : 'var(--text-primary)' }}>{stats.missing}</b></span>
          {humidStats && (
            <span>{th ? 'ความชื้นเกิน' : 'RH over'} <b style={{ color: humidStats.out > 0 ? 'var(--red)' : 'var(--text-primary)' }}>{humidStats.out}</b></span>
          )}
        </div>
      </div>

      {showSettings && (
        <div style={{
          display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
          border: '1px solid var(--glass-border)', borderRadius: 12, padding: 12, background: 'var(--bg-surface)',
        }}>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
            {th ? 'เวลาเช้า' : 'Morning'}
            <input type="time" value={amTime} onChange={(e) => setAmTime(e.target.value)} style={selectStyle} />
          </label>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
            {th ? 'เวลาบ่าย' : 'Afternoon'}
            <input type="time" value={pmTime} onChange={(e) => setPmTime(e.target.value)} style={selectStyle} />
          </label>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
            {th ? 'ยอมรับคลาดเคลื่อน (นาที)' : 'Tolerance (min)'}
            <input
              type="number" min={1} max={180} value={tol}
              onChange={(e) => setTol(Math.min(180, Math.max(1, Number(e.target.value) || 1)))}
              style={{ ...selectStyle, width: 80 }}
            />
          </label>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            {th
              ? 'ระบบจะเลือกค่าที่ใกล้เวลานี้ที่สุดภายในช่วงที่ยอมรับ ถ้าไม่มีจะเว้นว่างไว้'
              : 'The reading nearest each time inside the tolerance is used; nothing in the window leaves the slot blank.'}
          </span>
        </div>
      )}

      {devices.length === 0 ? (
        <div style={{
          border: '1px dashed var(--glass-border)', borderRadius: 16, padding: 40,
          textAlign: 'center', color: 'var(--text-muted)', fontSize: 13,
        }}>
          {th ? 'สถานที่นี้ยังไม่มีเครื่องวัดอุณหภูมิ' : 'This site has no temperature logger'}
        </div>
      ) : error ? (
        <div style={{
          border: '1px solid var(--glass-border)', borderRadius: 16, padding: 32,
          textAlign: 'center', color: 'var(--text-muted)', fontSize: 13,
        }}>
          {th ? 'โหลดข้อมูลไม่สำเร็จ' : 'Could not load the record'} — {error}
        </div>
      ) : !data || !tempView || tempView.lo == null || tempView.hi == null ? (
        <div style={{
          border: '1px dashed var(--glass-border)', borderRadius: 16, padding: 40,
          textAlign: 'center', color: 'var(--text-muted)', fontSize: 13,
        }}>
          {loading ? (th ? 'กำลังโหลด…' : 'Loading…') : (th ? 'ยังไม่ได้ตั้งเกณฑ์อุณหภูมิให้เครื่องวัดนี้' : 'No temperature range configured for this logger')}
        </div>
      ) : (
        <>
        {view === 'summary' && (
          <ModernReport temp={tempView} humid={humidView} days={days} dueSlots={dueSlots} amTime={amTime} pmTime={pmTime}
            th={th} loading={loading} devLabel={devLabel} online={device?.st === 'online'} monthLabel={monthLabel} year={th ? beYear : year} />
        )}
        {/* the paper form: shown in "form" view, always mounted because Print copies its markup */}
        <div ref={sheetsRef} style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .15s', display: view === 'form' ? 'block' : 'none' }}>
          {/* ── แผ่นที่ 1 — กราฟบันทึก ── */}
          <Sheet>
            <SheetHead
              plantName={plant.name} addr={plant.addr} devLabel={devLabel} macId={macId}
              monthLabel={monthLabel} beYear={beYear} metrics={metricViews} th={th}
              sheet={th ? 'แผ่นที่ 1/2 — กราฟบันทึก' : 'Sheet 1/2 — Chart'} subtitle={subtitle}
            />

            {metricViews.map((m) => (
              <div key={m.key}>
                {/* The metric name only earns a heading when there is more than
                    one — a temperature-only logger keeps the paper form's own
                    two headings and nothing else. */}
                {metricViews.length > 1 && (
                  <div style={{
                    fontSize: 13, fontWeight: 700, color: INK, marginTop: 18,
                    borderBottom: `1px solid ${GRID}`, paddingBottom: 4,
                  }}>
                    {th ? m.labelTh : m.labelEn} ({m.unit}) — {th ? 'เกณฑ์' : 'range'} {limitLabel(m, th)}
                  </div>
                )}
                {(['am', 'pm'] as const).map((slot) => (
                  <div key={slot} className="tr-chart" style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: INK, marginBottom: 6 }}>
                      {slot === 'am'
                        ? `${th ? 'เช้า — เวลา' : 'Morning —'} ${dotTime(amTime)}${th ? ' น.' : ''}`
                        : `${th ? 'บ่าย — เวลา' : 'Afternoon —'} ${dotTime(pmTime)}${th ? ' น.' : ''}`}
                    </div>
                    <GridChart metric={m} slot={slot} days={days} compact={printing} />
                  </div>
                ))}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap', marginTop: 12, fontSize: 11, color: MUTED }}>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 99, background: OK, marginRight: 5 }} />
                {th ? 'อยู่ในเกณฑ์' : 'In range'}</span>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 99, background: BAD, marginRight: 5 }} />
                {th ? 'นอกเกณฑ์' : 'Out of range'}</span>
              <span>{th ? 'ช่องว่าง = ไม่มีข้อมูลในช่วงเวลานั้น' : 'Blank = no reading in that window'}</span>
            </div>

            <Notes metrics={metricViews} amTime={amTime} pmTime={pmTime} tol={tol} th={th} />
            <SignRow th={th} />
          </Sheet>

          {/* ── แผ่นที่ 2 — ตารางบันทึก ── */}
          <Sheet>
            <SheetHead
              plantName={plant.name} addr={plant.addr} devLabel={devLabel} macId={macId}
              monthLabel={monthLabel} beYear={beYear} metrics={metricViews} th={th}
              sheet={th ? 'แผ่นที่ 2/2 — ตารางบันทึก' : 'Sheet 2/2 — Table'} subtitle={subtitle}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 14 }}>
              {[[1, 15], [16, days]].map(([from, to]) => (
                <DayTable
                  key={from} from={from} to={to}
                  temp={tempView} humid={humidView}
                  amTime={amTime} pmTime={pmTime} th={th}
                />
              ))}
            </div>

            <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 12, fontSize: 12, color: INK }}>
              <span><b>{th ? 'บันทึกได้:' : 'Recorded:'}</b> {stats.recorded}/{dueSlots} {th ? 'ครั้ง' : ''}</span>
              <span><b>{th ? 'อุณหภูมินอกเกณฑ์:' : 'Temp out of range:'}</b> <span style={{ color: stats.out > 0 ? BAD : INK }}>{stats.out}</span> {th ? 'ครั้ง' : ''}</span>
              <span><b>{th ? 'ไม่มีข้อมูล:' : 'Missing:'}</b> {stats.missing} {th ? 'ครั้ง' : ''}</span>
              <span><b>{th ? 'ต่ำสุด:' : 'Min:'}</b> {stats.min?.toFixed(1) ?? '—'} {tempView.unit}</span>
              <span><b>{th ? 'สูงสุด:' : 'Max:'}</b> {stats.max?.toFixed(1) ?? '—'} {tempView.unit}</span>
            </div>

            {humidView && humidStats && (
              <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 5, fontSize: 12, color: INK }}>
                <span><b>{th ? 'ความชื้นนอกเกณฑ์:' : 'RH out of range:'}</b> <span style={{ color: humidStats.out > 0 ? BAD : INK }}>{humidStats.out}</span> {th ? 'ครั้ง' : ''}</span>
                <span><b>{th ? 'ความชื้นต่ำสุด:' : 'RH min:'}</b> {humidStats.min?.toFixed(0) ?? '—'} {humidView.unit}</span>
                <span><b>{th ? 'ความชื้นสูงสุด:' : 'RH max:'}</b> {humidStats.max?.toFixed(0) ?? '—'} {humidView.unit}</span>
              </div>
            )}

            <Notes metrics={metricViews} amTime={amTime} pmTime={pmTime} tol={tol} th={th} />
            <SignRow th={th} />
          </Sheet>
        </div>
        </>
      )}
    </div>
  );
}

function DayTable({
  from, to, temp, humid, amTime, pmTime, th,
}: {
  from: number; to: number;
  temp: MetricView; humid?: MetricView;
  amTime: string; pmTime: string; th: boolean;
}) {
  const cell: React.CSSProperties = { border: `1px solid ${RULE}`, padding: '3px 5px', fontSize: 11.5, height: 22 };
  const head: React.CSSProperties = { ...cell, fontWeight: 700, textAlign: 'center', background: '#f4f4f4' };

  const valCell = (m: MetricView, s: SlotValue | null, key: string) => {
    if (!s) return <td key={key} style={{ ...cell, textAlign: 'center' }} />;
    const ok = inRange(s.value, m);
    return (
      <td key={key} style={{ ...cell, textAlign: 'center', color: ok ? OK : BAD, fontWeight: ok ? 400 : 700 }} title={s.time}>
        {s.value.toFixed(m.digits)}
      </td>
    );
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <thead>
        {humid && (
          // Two header rows only when there are two metrics to tell apart.
          <tr>
            <th style={{ ...head, width: 28 }} rowSpan={2}>{th ? 'วันที่' : 'Day'}</th>
            <th style={head} colSpan={2}>{th ? 'อุณหภูมิ (°C)' : 'Temp (°C)'}</th>
            <th style={head} colSpan={2}>{th ? 'ความชื้น (%RH)' : 'RH (%)'}</th>
            <th style={{ ...head, width: 62 }} rowSpan={2}>{th ? 'สถานะ' : 'Status'}</th>
            <th style={head} rowSpan={2}>{th ? 'หมายเหตุ / การแก้ไข' : 'Notes / action'}</th>
          </tr>
        )}
        <tr>
          {!humid && <th style={{ ...head, width: 34 }}>{th ? 'วันที่' : 'Day'}</th>}
          <th style={{ ...head, width: 50 }}>{amTime}</th>
          <th style={{ ...head, width: 50 }}>{pmTime}</th>
          {humid && <th style={{ ...head, width: 50 }}>{amTime}</th>}
          {humid && <th style={{ ...head, width: 50 }}>{pmTime}</th>}
          {!humid && <th style={{ ...head, width: 74 }}>{th ? 'สถานะ' : 'Status'}</th>}
          {!humid && <th style={head}>{th ? 'หมายเหตุ / การแก้ไข' : 'Notes / action'}</th>}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => {
          const day = from + i;
          const t = temp.rows.find((x) => x.day === day);
          const hRow = humid?.rows.find((x) => x.day === day);
          const tSlots = [t?.am, t?.pm].filter(Boolean) as SlotValue[];
          const anyOut = tSlots.some((s) => !inRange(s.value, temp));
          // No reading either time = nothing to state; the row stays blank for
          // the person completing it by hand rather than claiming "ปกติ".
          const status = tSlots.length === 0 ? '' : anyOut ? (th ? 'นอกเกณฑ์' : 'Out') : (th ? 'ปกติ' : 'Normal');
          return (
            <tr key={day}>
              <td style={{ ...cell, textAlign: 'center', fontWeight: 700 }}>{day}</td>
              {valCell(temp, t?.am ?? null, 'ta')}
              {valCell(temp, t?.pm ?? null, 'tp')}
              {humid && valCell(humid, hRow?.am ?? null, 'ha')}
              {humid && valCell(humid, hRow?.pm ?? null, 'hp')}
              <td style={{ ...cell, textAlign: 'center', color: anyOut ? BAD : OK, fontWeight: anyOut ? 700 : 400 }}>{status}</td>
              <td style={cell} />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── modern on-screen summary (theme-aware; never printed) ───────────────────

const C_AM = '#0ea5e9', C_PM = '#8b5cf6', C_OK = '#10b77f', C_BAD = '#ef4444', C_MISS = '#f59e0b';

function Card({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div style={{ flex: '1 1 160px', minWidth: 0, padding: '14px 16px', borderRadius: 14, background: 'var(--bg-surface)', border: '1px solid var(--glass-border)', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/** AM + PM over the month on one chart: the safe band shaded, out-of-range points red, gaps where nothing was recorded. */
function TrendChart({ m, days, amTime, pmTime, th }: { m: MetricView; days: number; amTime: string; pmTime: string; th: boolean }) {
  const W = 1000, H = 260, L = 46, R = 14, T = 14, B = 28;
  const vals = m.rows.flatMap((r) => [r.am?.value, r.pm?.value]).filter((v): v is number => v != null);
  const lo = m.lo ?? Math.min(...vals, 0), hi = m.hi ?? Math.max(...vals, 10);
  const yMin = Math.floor(Math.min(lo, ...vals) - Math.max(2, (hi - lo) * 0.25));
  const yMax = Math.ceil(Math.max(hi, ...vals) + Math.max(2, (hi - lo) * 0.25));
  const x = (d: number) => L + ((d - 1) / Math.max(1, days - 1)) * (W - L - R);
  const y = (v: number) => T + (1 - (v - yMin) / (yMax - yMin || 1)) * (H - T - B);
  const path = (slot: 'am' | 'pm') => {
    let d = '', pen = false;
    for (let day = 1; day <= days; day++) {
      const s = m.rows.find((r) => r.day === day)?.[slot];
      if (!s) { pen = false; continue; }
      d += `${pen ? 'L' : 'M'}${x(day).toFixed(1)},${y(s.value).toFixed(1)}`; pen = true;
    }
    return d;
  };
  const ticks: number[] = [];
  const step = Math.max(1, Math.ceil((yMax - yMin) / 6));
  for (let v = Math.ceil(yMin / step) * step; v <= yMax; v += step) ticks.push(v);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label={th ? 'กราฟอุณหภูมิรายวัน' : 'Daily readings'}>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--glass-border)" strokeWidth={1} />
          <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize={11} fill="var(--text-muted)">{v}</text>
        </g>
      ))}
      {m.lo != null && m.hi != null && (
        <rect x={L} width={W - L - R} y={y(m.hi)} height={Math.max(0, y(m.lo) - y(m.hi))} fill={C_OK} opacity={0.1} />
      )}
      {[m.lo, m.hi].map((v, i) => v != null && (
        <g key={i}>
          <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={C_OK} strokeDasharray="5 4" strokeWidth={1.4} />
          <text x={W - R - 4} y={y(v) + (i ? -5 : 13)} textAnchor="end" fontSize={11} fontWeight={700} fill={C_OK}>{v}{m.unit}</text>
        </g>
      ))}
      {Array.from({ length: days }, (_, i) => i + 1).filter((d) => d === 1 || d % 5 === 0 || d === days).map((d) => (
        <text key={d} x={x(d)} y={H - 8} textAnchor="middle" fontSize={11} fill="var(--text-muted)">{d}</text>
      ))}
      {(['am', 'pm'] as const).map((slot) => (
        <g key={slot}>
          <path d={path(slot)} fill="none" stroke={slot === 'am' ? C_AM : C_PM} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" />
          {m.rows.map((r) => {
            const s = r[slot];
            if (!s) return null;
            const ok = inRange(s.value, m);
            return <circle key={r.day} cx={x(r.day)} cy={y(s.value)} r={ok ? 3.4 : 5} fill={ok ? (slot === 'am' ? C_AM : C_PM) : C_BAD} stroke="var(--bg-surface)" strokeWidth={1.5}>
              <title>{`${r.day} · ${slot === 'am' ? amTime : pmTime} · ${s.value.toFixed(m.digits)} ${m.unit} (${s.time})`}</title>
            </circle>;
          })}
        </g>
      ))}
    </svg>
  );
}

function ModernReport({ temp, humid, days, dueSlots, amTime, pmTime, th, loading, devLabel, online, monthLabel, year }: {
  temp: MetricView; humid?: MetricView; days: number; dueSlots: number; amTime: string; pmTime: string; th: boolean;
  loading: boolean; devLabel: string; online: boolean; monthLabel: string; year: number;
}) {
  const slots = temp.rows.flatMap((r) => (['am', 'pm'] as const).map((k) => ({ day: r.day, k, s: r[k] })));
  const got = slots.filter((x) => x.s);
  const out = got.filter((x) => !inRange(x.s!.value, temp));
  const vals = got.map((x) => x.s!.value);
  const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : undefined;
  const pct = got.length ? Math.round(((got.length - out.length) / got.length) * 100) : 0;
  const missing = Math.max(0, dueSlots - got.length);
  const f = (v?: number) => (v == null ? '—' : v.toFixed(temp.digits));
  const devOf = (v: number) => (temp.hi != null && v > temp.hi ? v - temp.hi : temp.lo != null && v < temp.lo ? v - temp.lo : 0);
  const panel: React.CSSProperties = { padding: '16px 18px', borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--glass-border)' };
  const slotState = (day: number, k: 'am' | 'pm') => {
    const idx = (day - 1) * 2 + (k === 'pm' ? 1 : 0);
    const s = temp.rows.find((r) => r.day === day)?.[k];
    if (s) return inRange(s.value, temp) ? 'ok' : 'bad';
    return idx < dueSlots ? 'miss' : 'future';
  };
  const SC: Record<string, string> = { ok: C_OK, bad: C_BAD, miss: C_MISS, future: 'var(--glass-border)' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
      <div style={{ ...panel, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: 'var(--text-muted)' }}>{th ? 'รายงานอุณหภูมิประจำเดือน' : 'MONTHLY TEMPERATURE REPORT'}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{devLabel}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{monthLabel} {year} · {th ? 'เกณฑ์' : 'range'} {limitLabel(temp, th)}{!temp.fromDevice ? (th ? ' (ค่ามาตรฐานตามประเภทตู้)' : ' (default for this unit type)') : ''}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <svg width="74" height="74" viewBox="0 0 36 36" aria-hidden>
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="var(--glass-border)" strokeWidth="3.5" />
            <circle cx="18" cy="18" r="15.5" fill="none" stroke={pct >= 95 ? C_OK : pct >= 80 ? C_MISS : C_BAD} strokeWidth="3.5" strokeLinecap="round"
              strokeDasharray={`${(pct / 100) * 97.4} 97.4`} transform="rotate(-90 18 18)" />
            <text x="18" y="21" textAnchor="middle" fontSize="9" fontWeight="800" fill="var(--text-primary)">{got.length ? `${pct}%` : '—'}</text>
          </svg>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <div><b style={{ color: 'var(--text-primary)' }}>{th ? 'อยู่ในเกณฑ์' : 'In range'}</b></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: 8, background: online ? C_OK : C_MISS }} />{online ? (th ? 'เครื่องวัดออนไลน์' : 'Logger online') : (th ? 'เครื่องวัดออฟไลน์' : 'Logger offline')}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Card label={th ? 'บันทึกได้' : 'Recorded'} value={`${got.length}/${dueSlots}`} sub={th ? `เช้า ${amTime} · บ่าย ${pmTime}` : `AM ${amTime} · PM ${pmTime}`} color={C_AM} />
        <Card label={th ? 'นอกเกณฑ์' : 'Out of range'} value={String(out.length)} sub={out.length ? (th ? 'ดูรายการด้านล่าง' : 'see the list below') : (th ? 'ไม่มี' : 'none')} color={out.length ? C_BAD : C_OK} />
        <Card label={th ? 'ไม่มีข้อมูล' : 'Missing'} value={String(missing)} sub={th ? 'ช่องที่ต้องบันทึกด้วยมือ' : 'slots to fill by hand'} color={missing ? C_MISS : C_OK} />
        <Card label={th ? 'ต่ำสุด · เฉลี่ย · สูงสุด' : 'Min · avg · max'} value={`${f(vals.length ? Math.min(...vals) : undefined)} · ${f(avg)} · ${f(vals.length ? Math.max(...vals) : undefined)}`} sub={temp.unit} color={C_PM} />
      </div>

      <div style={panel}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8, flexWrap: 'wrap' }}>
          <b style={{ fontSize: 14, color: 'var(--text-primary)' }}>{th ? 'อุณหภูมิรายวัน' : 'Daily temperature'}</b>
          {[[C_AM, th ? `เช้า ${amTime}` : `AM ${amTime}`], [C_PM, th ? `บ่าย ${pmTime}` : `PM ${pmTime}`], [C_OK, th ? 'ช่วงที่กำหนด' : 'Target range'], [C_BAD, th ? 'นอกเกณฑ์' : 'Out of range']].map(([c, l]) => (
            <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: c }} />{l}
            </span>
          ))}
        </div>
        <TrendChart m={temp} days={days} amTime={amTime} pmTime={pmTime} th={th} />
      </div>

      {humid && (
        <div style={panel}>
          <b style={{ fontSize: 14, color: 'var(--text-primary)' }}>{th ? 'ความชื้นสัมพัทธ์' : 'Relative humidity'}</b>
          <TrendChart m={humid} days={days} amTime={amTime} pmTime={pmTime} th={th} />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        <div style={panel}>
          <b style={{ fontSize: 14, color: 'var(--text-primary)' }}>{th ? 'ปฏิทินการบันทึก' : 'Recording calendar'}</b>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '2px 0 10px' }}>{th ? 'แต่ละวัน: ซ้าย = เช้า · ขวา = บ่าย' : 'Each day: left = AM · right = PM'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
            {Array.from({ length: days }, (_, i) => i + 1).map((d) => (
              <div key={d} style={{ padding: '5px 6px', borderRadius: 8, border: '1px solid var(--glass-border)' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)' }}>{d}</div>
                <div style={{ display: 'flex', gap: 3, marginTop: 3 }}>
                  {(['am', 'pm'] as const).map((k) => <span key={k} style={{ flex: 1, height: 7, borderRadius: 4, background: SC[slotState(d, k)] }} />)}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, fontSize: 11.5, color: 'var(--text-secondary)' }}>
            {[['ok', th ? 'ปกติ' : 'OK'], ['bad', th ? 'นอกเกณฑ์' : 'Out'], ['miss', th ? 'ไม่มีข้อมูล' : 'Missing'], ['future', th ? 'ยังไม่ถึง' : 'Upcoming']].map(([k, l]) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 14, height: 7, borderRadius: 4, background: SC[k] }} />{l}</span>
            ))}
          </div>
        </div>
        <div style={panel}>
          <b style={{ fontSize: 14, color: 'var(--text-primary)' }}>{th ? 'รายการนอกเกณฑ์' : 'Out-of-range readings'}</b>
          {out.length === 0 ? (
            <div style={{ marginTop: 18, fontSize: 13, color: C_OK, fontWeight: 700 }}>{th ? '✓ ทุกครั้งที่บันทึกอยู่ในเกณฑ์' : '✓ Every recorded reading is in range'}</div>
          ) : (
            <div style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
              {out.map((o) => {
                const v = o.s!.value, dv = devOf(v);
                return (
                  <div key={`${o.day}${o.k}`} style={{ display: 'grid', gridTemplateColumns: '70px 1fr auto', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid var(--glass-border)', fontSize: 12.5 }}>
                    <b style={{ color: 'var(--text-primary)' }}>{th ? 'วันที่' : 'Day'} {o.day}</b>
                    <span style={{ color: 'var(--text-secondary)' }}>{o.k === 'am' ? (th ? 'เช้า' : 'AM') : (th ? 'บ่าย' : 'PM')} · {o.s!.time}</span>
                    <span style={{ fontWeight: 800, color: C_BAD, fontVariantNumeric: 'tabular-nums' }}>{v.toFixed(temp.digits)} {temp.unit} <span style={{ fontSize: 11, fontWeight: 600 }}>({dv > 0 ? '+' : ''}{dv.toFixed(1)})</span></span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
